import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@/components/ui/button';
import { FullScreenDialog } from '@/components/ui/full-screen-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import OrganizationChart from 'components/IncidentConfig/OrganizationChart';
import {
  RosterDraftProvider,
  useCreateRosterDraft,
} from 'components/IncidentConfig/CreateIncident/CreateRosterDraftContext';
import RosterActions from 'components/IncidentConfig/roster/RosterActions';
import type {
  DraftRosterAssignment,
  DraftRosterSeat,
} from 'components/IncidentConfig/CreateIncident/createIncidentModel';
import { orgTemplateIdFromSlug } from 'components/IncidentConfig/roster/templates/rosterTemplateCatalog';
import type { RosterTemplateNodeDto } from 'components/IncidentConfig/roster/templates/rosterTemplateTypes';
import { useCreateRosterTemplate } from 'hooks/rosterTemplateLibrary/rosterTemplateLibraryHooks';
import {
  useSaveFilledRosterTemplate,
  type FilledRosterTemplate,
} from 'hooks/rosterTemplateLibrary/filledRosterTemplateHooks';
import { selectGroupGuid } from 'slices/commonSelectors';
import { useHwcgFlag } from 'utils/featureFlags';
import {
  draftAssignmentsFromFilled,
  filledAssignmentsFromDraft,
} from '../RosterTemplatesTab/filledRosterAssignments';
import {
  duplicateSeatNames,
  nodesFromSeats,
  seatsFromNodes,
} from '../RosterTemplatesTab/rosterTemplateSeats';

const NO_ASSIGNMENTS: DraftRosterAssignment[] = [];

export type PlanRosterDialogSeed = {
  existingFilled?: FilledRosterTemplate | null;
  /** Structure nodes for the filled template's source chart. */
  structureNodes?: RosterTemplateNodeDto[];
  /** Open directly in Fill Template mode. */
  startInFillMode?: boolean;
};

/**
 * The roster chart with its Templates panel, for choosing a plan's roster
 * templates. Loading a saved organization template adds it to the plan; a chart
 * built here is added once it is saved as a template. HWCG also offers Fill
 * Template to assign people/POCs and link a filled roster on the plan.
 */
export function PlanRosterDialog({
  planName,
  onAddTemplate,
  onFilledTemplate,
  seed,
  onClose,
}: {
  planName: string;
  onAddTemplate: (templateId: string) => void;
  /** HWCG: called after a filled roster is saved so the plan can link it. */
  onFilledTemplate?: (templateId: string) => void;
  seed?: PlanRosterDialogSeed;
  onClose: () => void;
}) {
  const hwcg = useHwcgFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const createTemplate = useCreateRosterTemplate();
  const saveFilled = useSaveFilledRosterTemplate();
  const existingFilled = seed?.existingFilled ?? null;

  const [seats, setSeats] = useState<DraftRosterSeat[]>(() =>
    seed?.structureNodes?.length
      ? seatsFromNodes(seed.structureNodes)
      : []
  );
  const [assignments, setAssignments] = useState<DraftRosterAssignment[]>(
    () => {
      if (!existingFilled || !seed?.structureNodes?.length) return [];
      return draftAssignmentsFromFilled(
        seatsFromNodes(seed.structureNodes),
        existingFilled.assignments
      );
    }
  );
  const [name, setName] = useState(
    existingFilled?.name ?? ''
  );
  const [description, setDescription] = useState('');
  const [fillMode, setFillMode] = useState(
    Boolean(hwcg && seed?.startInFillMode)
  );
  const [sourceTemplateId, setSourceTemplateId] = useState<string | null>(
    existingFilled?.sourceTemplateId ?? null
  );
  const [sourceSlug, setSourceSlug] = useState<string | null>(
    existingFilled?.sourceSlug ?? null
  );
  const [filledId, setFilledId] = useState<string | undefined>(
    existingFilled?.id
  );

  const handleChange = useCallback(
    (nextSeats: DraftRosterSeat[], nextAssignments: DraftRosterAssignment[]) => {
      setSeats(nextSeats);
      setAssignments(nextAssignments);
    },
    []
  );

  const handleTemplateLoaded = useCallback(
    (slug: string) => {
      const templateId = orgTemplateIdFromSlug(slug);
      setSourceSlug(slug);
      setSourceTemplateId(templateId);
      if (templateId) onAddTemplate(templateId);
    },
    [onAddTemplate]
  );

  const duplicates = useMemo(() => duplicateSeatNames(seats), [seats]);
  const hasBlankSeat = seats.some((seat) => !seat.name.trim());
  const canSaveStructure =
    !!name.trim() &&
    seats.length > 0 &&
    !hasBlankSeat &&
    duplicates.length === 0 &&
    !createTemplate.isPending;

  const canSaveFilled =
    hwcg &&
    fillMode &&
    !!name.trim() &&
    seats.length > 0 &&
    !hasBlankSeat &&
    duplicates.length === 0 &&
    !saveFilled.isPending &&
    !createTemplate.isPending;

  const startNewRoster = () => {
    setSeats([]);
    setAssignments([]);
    setName('');
    setDescription('');
    setFillMode(false);
    setSourceTemplateId(null);
    setSourceSlug(null);
    setFilledId(undefined);
  };

  const saveAsTemplate = () => {
    createTemplate.mutate(
      {
        name: name.trim(),
        description: description.trim() || null,
        attachOnly: false,
        nodes: nodesFromSeats(seats),
      },
      {
        onSuccess: (created) => {
          onAddTemplate(created.id);
          setSourceTemplateId(created.id);
          setSourceSlug(`org:${created.id}`);
        },
      }
    );
  };

  const enterFillMode = () => {
    if (!hwcg) return;
    setFillMode(true);
    if (!name.trim()) {
      setName(existingFilled?.name ?? `${planName} roster`);
    }
  };

  const persistFilled = (
    asNew: boolean,
    source: { sourceTemplateId: string | null; sourceSlug: string | null }
  ) => {
    if (!onFilledTemplate) return;
    saveFilled.mutate(
      {
        id: asNew ? undefined : filledId,
        name: name.trim(),
        sourceTemplateId: source.sourceTemplateId,
        sourceSlug: source.sourceSlug,
        assignments: filledAssignmentsFromDraft(seats, assignments),
      },
      {
        onSuccess: (created) => {
          setFilledId(created.id);
          onFilledTemplate(created.id);
        },
      }
    );
  };

  const saveFilledTemplate = (asNew: boolean) => {
    if (!hwcg || !fillMode || !onFilledTemplate) return;
    if (
      !name.trim() ||
      seats.length === 0 ||
      hasBlankSeat ||
      duplicates.length > 0 ||
      saveFilled.isPending ||
      createTemplate.isPending
    ) {
      return;
    }
    if (sourceTemplateId || sourceSlug) {
      persistFilled(asNew, { sourceTemplateId, sourceSlug });
      return;
    }
    // Chart was built in-dialog without a saved structure — persist one first.
    createTemplate.mutate(
      {
        name: name.trim(),
        description: description.trim() || null,
        attachOnly: false,
        nodes: nodesFromSeats(seats),
      },
      {
        onSuccess: (created) => {
          onAddTemplate(created.id);
          const nextSource = {
            sourceTemplateId: created.id,
            sourceSlug: `org:${created.id}`,
          };
          setSourceTemplateId(nextSource.sourceTemplateId);
          setSourceSlug(nextSource.sourceSlug);
          persistFilled(asNew, nextSource);
        },
      }
    );
  };

  if (!groupGuid) return null;

  return (
    <RosterDraftProvider
      groupGuid={groupGuid}
      seats={seats}
      assignments={fillMode ? assignments : NO_ASSIGNMENTS}
      creator={null}
      structureOnly={!fillMode}
      onChange={handleChange}
      onTemplateLoaded={handleTemplateLoaded}
    >
      <FullScreenDialog
        open
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
        title={`Roster templates for ${planName}`}
        description={
          fillMode
            ? 'Assign people and resource points of contact on each seat, then save as a filled template for this plan.'
            : 'Load a saved template from the Templates panel to add it to this plan, or build a chart and save it as a template. Use Fill Template to assign people.'
        }
        bodyClassName="tw:!flex tw:!min-h-0 tw:!flex-col tw:!overflow-hidden tw:!p-3"
        header={
          <div className="tw:grid tw:gap-3 tw:md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] tw:md:items-end">
            <div className="tw:flex tw:flex-col tw:gap-1">
              <Label htmlFor="plan-roster-template-name">Template name</Label>
              <Input
                id="plan-roster-template-name"
                value={name}
                maxLength={200}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div className="tw:flex tw:flex-col tw:gap-1">
              <Label htmlFor="plan-roster-template-description">
                Description
              </Label>
              <Textarea
                id="plan-roster-template-description"
                value={description}
                rows={1}
                className="tw:min-h-9 tw:resize-none"
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
          </div>
        }
        footer={
          <>
            {duplicates.length > 0 ? (
              <p className="tw:mr-auto tw:m-0 tw:text-xs tw:text-destructive">
                Seat names must be unique. Rename: {duplicates.join(', ')}
              </p>
            ) : hasBlankSeat ? (
              <p className="tw:mr-auto tw:m-0 tw:text-xs tw:text-destructive">
                Every seat needs a name.
              </p>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={createTemplate.isPending || saveFilled.isPending}
              onClick={startNewRoster}
            >
              New roster
            </Button>
            {!fillMode ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!canSaveStructure}
                onClick={saveAsTemplate}
              >
                {createTemplate.isPending ? 'Saving…' : 'Save as template'}
              </Button>
            ) : null}
            {hwcg && !fillMode ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={
                  seats.length === 0 ||
                  hasBlankSeat ||
                  duplicates.length > 0
                }
                onClick={enterFillMode}
              >
                Fill Template
              </Button>
            ) : null}
            {hwcg && fillMode ? (
              <>
                {filledId ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!canSaveFilled}
                    onClick={() => saveFilledTemplate(true)}
                  >
                    {saveFilled.isPending ? 'Saving…' : 'Save as new'}
                  </Button>
                ) : null}
                <Button
                  type="button"
                  size="sm"
                  disabled={!canSaveFilled}
                  onClick={() => saveFilledTemplate(false)}
                >
                  {saveFilled.isPending
                    ? 'Saving…'
                    : filledId
                      ? 'Save filled template'
                      : 'Save filled template'}
                </Button>
              </>
            ) : null}
            <Button type="button" size="sm" variant="outline" onClick={onClose}>
              Done
            </Button>
          </>
        }
      >
        <PlanRosterChartBody fillMode={fillMode} />
      </FullScreenDialog>
    </RosterDraftProvider>
  );
}

function PlanRosterChartBody({ fillMode }: { fillMode: boolean }) {
  const { members } = useCreateRosterDraft();
  return (
    <>
      {fillMode ? <RosterActions /> : null}
      <div className="tw:min-h-0 tw:flex-1 tw:overflow-auto">
        <OrganizationChart incidentMembers={fillMode ? members : []} />
      </div>
    </>
  );
}
